import { z } from "zod";

export const manuscriptChangeApproveMetadataSchema = z.object({
  changeId: z.string().min(1),
  documentId: z.string().min(1).optional(),
  projectId: z.string().min(1).optional(),
  chatId: z.string().min(1).optional(),
}).strict();

export type ManuscriptChangeApproveMetadata = z.infer<
  typeof manuscriptChangeApproveMetadataSchema
>;

export function parseManuscriptChangeApproveMetadata(
  raw: unknown,
): ManuscriptChangeApproveMetadata | null {
  const parsed = manuscriptChangeApproveMetadataSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/** True when the composer line is a brief rejection of a visible suggestion card. */
export function userMessageLooksLikeManuscriptChangeDecline(text: string): boolean {
  const trimmed = String(text ?? "").trim();
  if (!trimmed || trimmed.length > 120) return false;
  if (/[?"“”`\[\]]/.test(trimmed)) return false;
  const normalized = trimmed.toLowerCase();
  return (
    normalized === "decline"
    || /^(?:no|nope|nah|cancel|reject|skip)(?:[,.!\s]|$)/i.test(trimmed)
    || /^(?:no thanks|not now|never mind|leave it)\b/i.test(trimmed)
  );
}

type MessageWithMetadata = {
  role?: string;
  metadata?: unknown;
};

function readManuscriptChangeStatus(metadata: unknown): string | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const manuscriptChange = (metadata as { manuscriptChange?: unknown }).manuscriptChange;
  if (!manuscriptChange || typeof manuscriptChange !== "object" || Array.isArray(manuscriptChange)) {
    return null;
  }
  const status = (manuscriptChange as { status?: unknown }).status;
  return typeof status === "string" ? status.trim() : null;
}

/** Latest assistant turn before the newest user message with a typed awaiting_consent card. */
export function conversationPriorAssistantAwaitingConsentManuscriptChange(
  conversation: MessageWithMetadata[],
): boolean {
  if (!Array.isArray(conversation) || conversation.length < 2) return false;
  let seenLatestUser = false;
  for (let i = conversation.length - 1; i >= 0; i -= 1) {
    const msg = conversation[i];
    if (!seenLatestUser) {
      if (msg?.role === "user") seenLatestUser = true;
      continue;
    }
    if (msg?.role !== "assistant") continue;
    const status = readManuscriptChangeStatus(msg.metadata);
    // The runner may insert assistant routing/tool messages between the saved
    // suggestion card and the user's reply. Those messages do not replace the
    // visible card and must not hide its typed lifecycle metadata.
    if (status === null) continue;
    return status === "awaiting_consent";
  }
  return false;
}

export function readAwaitingConsentManuscriptChangeId(
  metadata: unknown,
): string | null {
  if (!metadata || typeof metadata !== "object" || Array.isArray(metadata)) return null;
  const manuscriptChange = (metadata as { manuscriptChange?: unknown }).manuscriptChange;
  if (!manuscriptChange || typeof manuscriptChange !== "object" || Array.isArray(manuscriptChange)) {
    return null;
  }
  const status = (manuscriptChange as { status?: unknown }).status;
  const changeId = (manuscriptChange as { changeId?: unknown }).changeId;
  if (status !== "awaiting_consent" && status !== "ready_to_apply") return null;
  return typeof changeId === "string" && changeId.trim() ? changeId.trim() : null;
}

/** Newest assistant message (before the latest user turn) with a typed awaiting_consent card. */
export function findAwaitingConsentManuscriptChangeApproveTarget(
  messages: MessageWithMetadata[],
): ManuscriptChangeApproveMetadata | null {
  if (!Array.isArray(messages) || messages.length < 1) return null;
  let seenLatestUser = false;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const msg = messages[i];
    if (!seenLatestUser) {
      if (msg?.role === "user") seenLatestUser = true;
      continue;
    }
    if (msg?.role !== "assistant") continue;
    const changeId = readAwaitingConsentManuscriptChangeId(msg.metadata);
    if (!changeId) return null;
    const manuscriptChange = (msg.metadata as { manuscriptChange?: { documentId?: unknown } })
      .manuscriptChange;
    const documentId = typeof manuscriptChange?.documentId === "string"
      ? manuscriptChange.documentId.trim()
      : undefined;
    return {
      changeId,
      ...(documentId ? { documentId } : {}),
    };
  }
  return null;
}
